import { createContext, useCallback, useEffect, useState } from 'react';

import { screenWidthAtom } from 'atoms/screenAtom';
import { useAtomValue } from 'jotai';

import { useModals } from '../ModalsContext';

import {
  ComplaintDeviceContextData,
  ComplaintDeviceProviderProps,
} from './types';

export const MOBILE_BREAKPOINT = 1024; // Yoga breakpoint max width for medium devices

export const ComplaintDeviceContextDefaultValues: ComplaintDeviceContextData = {
  isComplaintDevice: false,
  handleSetComplaintDevice: () => null,
};

export const ComplaintDeviceContext = createContext(
  ComplaintDeviceContextDefaultValues,
);

export function ComplaintDeviceProvider({
  children,
}: ComplaintDeviceProviderProps) {
  const [isComplaint, setIsComplaint] = useState(false);

  const { openModal, closeModal } = useModals();
  const screenWidth = useAtomValue(screenWidthAtom);

  const isMobile = screenWidth < MOBILE_BREAKPOINT;

  function handleSetComplaint(value: boolean) {
    setIsComplaint(value);
  }

  const handleDeviceWarning = useCallback(() => {
    openModal('deviceWarning', {
      props: {
        onConsent: () => {
          handleSetComplaint(true);
          closeModal('deviceWarning');
        },
      },
    });
  }, [closeModal, openModal]);

  const handleResize = useCallback(() => {
    if (isMobile) {
      handleDeviceWarning();
      return;
    }

    handleSetComplaint(true);
  }, [handleDeviceWarning, isMobile]);

  useEffect(() => {
    if (isComplaint) {
      closeModal('deviceWarning');
      return;
    }

    if (!isComplaint) {
      handleResize();
    }
  }, [closeModal, handleResize, isComplaint]);

  return (
    <ComplaintDeviceContext.Provider
      value={{
        isComplaintDevice: isComplaint,
        handleSetComplaintDevice: handleSetComplaint,
      }}
    >
      {children}
    </ComplaintDeviceContext.Provider>
  );
}
